import { useState } from "react";

import {
  EDispatchNoteDesign,
  EPrintFormatCategory,
  IPrintFormat,
  IPrintProfile,
} from "dto/Printing/IOutgoingPrint";
import {
  IPaperChoice,
  IParcelPrintOptions,
  IPrintPreferences,
  PAPER_SIZES,
  PaperKey,
} from "utils/print/printPreferences";

import styles from "./styles.module.scss";

type Tab = "letters" | "stickers" | "envelopes" | "dispatchNotes";

const TABS: { key: Tab; label: string }[] = [
  { key: "letters", label: "Pisma" },
  { key: "stickers", label: "Naklejki" },
  { key: "envelopes", label: "Koperty" },
  { key: "dispatchNotes", label: "Zwrotki ZPO" },
];

const mm = (v?: number | null) =>
  v == null ? "" : `${Number(v).toLocaleString("pl-PL", { maximumFractionDigits: 2 })}`;

const formatLabel = (f: IPrintFormat) =>
  `${f.Name}${f.Name.includes("×") ? "" : ` (${mm(f.WidthMm)} × ${mm(f.HeightMm)} mm)`}`;

interface Props {
  prefs: IPrintPreferences;
  onChange: (prefs: IPrintPreferences) => void;
  formats: IPrintFormat[];
  profiles: IPrintProfile[];
}

/**
 * Ustawienia wydruku per kategoria — strona „Ustawienia" ModalPrintManagera z Magistratu
 * (BasicCategorySettings / AddressStickersCategorySettings / EnvelopesCategorySettings /
 * DispatchNotesCategorySettings). Drukarka i dwustronność są w oknie drukowania przeglądarki.
 */
const PrintPreferencesForm = ({ prefs, onChange, formats, profiles }: Props) => {
  const [tab, setTab] = useState<Tab>("stickers");

  const stickerFormats = formats.filter((f) => f.Category === EPrintFormatCategory.AddressSticker);
  const envelopeFormats = formats.filter((f) => f.Category === EPrintFormatCategory.Envelope);

  const patch = <K extends Tab>(key: K, value: Partial<IPrintPreferences[K]>) =>
    onChange({ ...prefs, [key]: { ...prefs[key], ...value } } as IPrintPreferences);

  const formatSelect = (
    list: IPrintFormat[],
    value: string | null | undefined,
    onSelect: (id: string | null) => void
  ) => {
    const current = list.find((f) => f.Id === value) ?? list.find((f) => f.IsDefault) ?? list[0];
    return (
      <div className={styles.field}>
        <label>Format wydruku</label>
        <select value={current?.Id ?? ""} onChange={(e) => onSelect(e.target.value || null)}>
          {list.length === 0 && <option value="">Brak formatów — dodaj w ustawieniach firmy</option>}
          {list.map((f) => (
            <option key={f.Id} value={f.Id}>
              {formatLabel(f)}
              {f.IsDefault ? " — domyślny" : ""}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const paperFields = (choice: IPaperChoice, onPatch: (v: Partial<IPaperChoice>) => void, profileId?: string | null, onProfile?: (id: string | null) => void) => (
    <>
      <div className={styles.field}>
        <label>Rozmiar papieru w drukarce</label>
        <select value={choice.paper} onChange={(e) => onPatch({ paper: e.target.value as PaperKey })}>
          {PAPER_SIZES.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      {choice.paper === "custom" && (
        <div className={styles.twoCol}>
          <div className={styles.field}>
            <label>Szerokość papieru (mm)</label>
            <input
              type="number"
              min={10}
              max={500}
              value={choice.customWidth ?? ""}
              onChange={(e) => onPatch({ customWidth: e.target.value === "" ? null : Number(e.target.value) })}
            />
          </div>
          <div className={styles.field}>
            <label>Wysokość papieru (mm)</label>
            <input
              type="number"
              min={10}
              max={500}
              value={choice.customHeight ?? ""}
              onChange={(e) => onPatch({ customHeight: e.target.value === "" ? null : Number(e.target.value) })}
            />
          </div>
        </div>
      )}
      {onProfile && (
        <div className={styles.field}>
          <label>Profil wydruku</label>
          <select
            value={profileId ?? ""}
            onChange={(e) => onProfile(e.target.value || null)}
            disabled={choice.paper === "format"}
          >
            <option value="">Domyślne ułożenie</option>
            {profiles.map((p) => (
              <option key={p.Id} value={p.Id}>
                {p.Name}
              </option>
            ))}
          </select>
          <span className={styles.hint}>
            {choice.paper === "format"
              ? "Profil (kalibracja ułożenia na papierze) działa po wybraniu rozmiaru papieru."
              : "Wyrównanie, przesunięcie i obrót wydruku na papierze — profile definiuje się w ustawieniach firmy."}
          </span>
        </div>
      )}
    </>
  );

  const optionChecks = (
    value: IParcelPrintOptions & { qr?: boolean },
    onPatch: (v: Partial<IParcelPrintOptions & { qr: boolean }>) => void,
    withQr: boolean
  ) => (
    <fieldset className={styles.group}>
      <legend>Drukuj</legend>
      <label className={styles.check}>
        <input type="checkbox" checked={value.caseSignature} onChange={(e) => onPatch({ caseSignature: e.target.checked })} />
        Sygnaturę teczki
      </label>
      <label className={styles.check}>
        <input type="checkbox" checked={value.parcelSignature} onChange={(e) => onPatch({ parcelSignature: e.target.checked })} />
        Sygnaturę przesyłki
      </label>
      <label className={styles.check}>
        <input type="checkbox" checked={value.title} onChange={(e) => onPatch({ title: e.target.checked })} />
        Tytuł przesyłki
      </label>
      {withQr && (
        <label className={styles.check}>
          <input type="checkbox" checked={!!value.qr} onChange={(e) => onPatch({ qr: e.target.checked })} />
          Kod QR
        </label>
      )}
    </fieldset>
  );

  const s = prefs.stickers;
  const e = prefs.envelopes;
  const d = prefs.dispatchNotes;

  return (
    <div className={styles.settings}>
      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "letters" && (
        <div className={styles.tabBody}>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={prefs.letters.duplex}
              onChange={(ev) => patch("letters", { duplex: ev.target.checked })}
            />
            Wydruk dwustronny — każde pismo od nowej kartki
          </label>
          <div className={styles.hint}>
            Pisma są łączone w jeden plik. Przy druku dwustronnym po piśmie o nieparzystej liczbie stron
            dodawana jest pusta strona. Drukarkę i tryb dwustronny wybierasz w oknie drukowania.
          </div>
        </div>
      )}

      {tab === "stickers" && (
        <div className={styles.tabBody}>
          {formatSelect(stickerFormats, s.formatId, (id) => patch("stickers", { formatId: id }))}
          <div className={styles.radios}>
            <label className={styles.check}>
              <input type="radio" checked={!s.sheet} onChange={() => patch("stickers", { sheet: false })} />
              Pojedyncze naklejki
            </label>
            <label className={styles.check}>
              <input type="radio" checked={s.sheet} onChange={() => patch("stickers", { sheet: true })} />
              Arkusz naklejek A4
            </label>
          </div>
          {s.sheet && (
            <div className={styles.twoCol}>
              <div className={styles.field}>
                <label>Orientacja arkusza</label>
                <select value={s.landscape ? "1" : "0"} onChange={(ev) => patch("stickers", { landscape: ev.target.value === "1" })}>
                  <option value="0">Pionowa</option>
                  <option value="1">Pozioma</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Liczba naklejek do pominięcia</label>
                <input
                  type="number"
                  min={0}
                  max={99}
                  value={s.skip}
                  onChange={(ev) => patch("stickers", { skip: Math.max(0, Number(ev.target.value) || 0) })}
                />
              </div>
            </div>
          )}
          <div className={styles.twoCol}>
            <div className={styles.field}>
              <label>Rozmiar czcionki (pt)</label>
              <input
                type="number"
                min={5}
                max={36}
                step={0.5}
                value={s.fontSize}
                onChange={(ev) => patch("stickers", { fontSize: Number(ev.target.value) || 12 })}
              />
            </div>
            <div className={styles.field}>
              <label>Liczba kopii</label>
              <input
                type="number"
                min={1}
                max={50}
                value={s.quantity}
                onChange={(ev) => patch("stickers", { quantity: Math.min(50, Math.max(1, Number(ev.target.value) || 1)) })}
              />
            </div>
          </div>
          {paperFields(s, (v) => patch("stickers", v), s.profileId, (id) => patch("stickers", { profileId: id }))}
          {optionChecks(s, (v) => patch("stickers", v), true)}
        </div>
      )}

      {tab === "envelopes" && (
        <div className={styles.tabBody}>
          {formatSelect(envelopeFormats, e.formatId, (id) => patch("envelopes", { formatId: id }))}
          {paperFields(e, (v) => patch("envelopes", v), e.profileId, (id) => patch("envelopes", { profileId: id }))}
          {optionChecks(e, (v) => patch("envelopes", v), false)}
          <label className={styles.check}>
            <input type="checkbox" checked={e.sender} onChange={(ev) => patch("envelopes", { sender: ev.target.checked })} />
            Drukuj nadawcę (bez frankownicy / nadruku na kopercie)
          </label>
        </div>
      )}

      {tab === "dispatchNotes" && (
        <div className={styles.tabBody}>
          <div className={styles.field}>
            <label>Wzór druku</label>
            <select
              value={d.design}
              onChange={(ev) => patch("dispatchNotes", { design: Number(ev.target.value) as EDispatchNoteDesign })}
            >
              <option value={EDispatchNoteDesign.Yellow}>Żółty — potwierdzenie odbioru Poczty Polskiej</option>
              <option value={EDispatchNoteDesign.White}>Biały — KPA / Ordynacja podatkowa</option>
            </select>
            <span className={styles.hint}>
              Dane są nadrukowywane na gotowy formularz 160 × 101 mm. Zwrotki drukują się dla przesyłek
              w formie z potwierdzeniem odbioru (np. list ZPO).
            </span>
          </div>
          {paperFields(d, (v) => patch("dispatchNotes", v), d.profileId, (id) => patch("dispatchNotes", { profileId: id }))}
          {optionChecks(d, (v) => patch("dispatchNotes", v), true)}
        </div>
      )}
    </div>
  );
};

export default PrintPreferencesForm;
